/**
 * Server-renderable slots of ProductCard — pure projections of the SCAPI Product (via `useProduct()`),
 * no `'use client'`, so they stay out of the PLP bundle when the consumer page is server-rendered.
 * Slots needing a client boundary (Action, AddToBagButton, Attribute, MatchScore, Rating) live in
 * `./product-card.client` and are re-exported through the `ProductCard` namespace below.
 */
import {
    Children,
    Fragment,
    isValidElement,
    type ComponentPropsWithRef,
    type ReactElement,
    type ReactNode,
    type Ref,
} from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/currency';
import { getPrimaryProductImageUrl } from '@/lib/product/product-utils';
import { DynamicImage } from '@/components/dynamic-image';
import type { DynamicImageDimensions } from '@/lib/images/dynamic-image';
import { useDynamicImageContext } from '@/providers/dynamic-image';
import { useProduct } from '@/providers/product-context';
import { Badge, Typography, type TypographyProps } from '@/shared-ui';
import { getPriceData } from '@/components/product-price/utils';
import { Price as PriceDisplay } from '@/shared-ui/molecules/price';
import { getProductCardBadges } from './lib/product-card-helpers';
import { Action, AddToBagButton, Attribute, MatchScore, Rating } from './product-card.client';

/**
 * Absorbs the ref cast in one place: Typography hard-types `ref` to `Ref<HTMLDivElement>` regardless of
 * its `as` prop, so the narrower slot refs below would otherwise need scattered `as never`. Drop once
 * Typography's ref becomes polymorphic on `as`.
 */
const asTypographyRef = (ref: Ref<HTMLElement> | undefined): Ref<HTMLDivElement> | undefined =>
    ref as Ref<HTMLDivElement> | undefined;

// Root

type RootProps = ComponentPropsWithRef<'article'> & {
    /**
     * `landscape` flips the card horizontal: content left, image right, icon add-to-bag bottom-right
     * (compose `AddToBagButton`, not `Action` — landscape has no full-width CTA). Child slots read it
     * via `group-data-[layout=landscape]/product-card:` — no context.
     */
    layout?: 'default' | 'landscape';
};

const Root = ({ layout = 'default', className, ref, ...props }: RootProps) => (
    <article
        ref={ref}
        data-slot="product-card-root"
        data-layout={layout}
        className={cn(
            'group/product-card flex w-full min-w-0 flex-col overflow-hidden bg-background',
            'data-[layout=landscape]:relative data-[layout=landscape]:flex-row data-[layout=landscape]:items-center data-[layout=landscape]:rounded-sm',
            className
        )}
        {...props}
    />
);
Root.displayName = 'ProductCard.Root';

// Image

/**
 * Slots that render on top of the image (not below). Matched by component identity, NOT `displayName`:
 * `AddToBagButton` crosses the `'use client'` boundary and loses a readable `displayName`, so a string
 * match wrongly bucketed it "below". Invisible to `pnpm test` — vitest skips the client transform, so
 * `displayName` survives there.
 */
const isImageAreaOverlay = (type: unknown) => type === Badges || type === AddToBagButton;

function flattenImageChildren(children: ReactNode): ReactNode[] {
    const flattened: ReactNode[] = [];

    Children.forEach(children, (child) => {
        if (isValidElement(child) && child.type === Fragment) {
            const fragment = child as ReactElement<{ children?: ReactNode }>;
            flattened.push(...flattenImageChildren(fragment.props.children));
            return;
        }

        flattened.push(child);
    });

    return flattened;
}

function partitionImageChildren(children: ReactNode): { overlay: ReactNode[]; below: ReactNode[] } {
    const overlay: ReactNode[] = [];
    const below: ReactNode[] = [];

    for (const child of flattenImageChildren(children)) {
        if (isValidElement(child) && isImageAreaOverlay(child.type)) {
            overlay.push(child);
            continue;
        }

        below.push(child);
    }

    return { overlay, below };
}

interface ImageProps extends Omit<ComponentPropsWithRef<'div'>, 'children'> {
    src?: string;
    alt?: string;
    aspectRatio?: number;
    children?: ReactNode;
    /**
     * Eager-load the LCP candidate (first above-the-fold tile) with `fetchpriority="high"`. Default
     * `false` keeps below-the-fold tiles lazy so a 60-tile PLP doesn't fire 60 requests at once.
     */
    priority?: boolean;
    /**
     * DIS sizes for this tile. Prefer declaring them once per rail with `<DynamicImageProvider>` —
     * the card is reused at very different sizes (152px search tile, 90px landscape), so the box is
     * only known where the layout is.
     */
    widths?: DynamicImageDimensions;
    heights?: DynamicImageDimensions;
    imageClassName?: string;
}

/** Safety net for a consumer that declares neither — a tile-sized request, not a full-res master. */
const DEFAULT_IMAGE_WIDTHS: DynamicImageDimensions = [320];

const Image = ({
    src,
    alt,
    aspectRatio = 1,
    priority = false,
    widths,
    heights,
    imageClassName,
    children,
    className,
    style,
    ref,
    ...props
}: ImageProps) => {
    const product = useProduct();
    // DynamicImage reads only `hasSource` from this context, never widths/heights — bridging them
    // onto the prop is what makes a rail's <DynamicImageProvider> actually take effect.
    const imageContext = useDynamicImageContext();
    const resolvedSrc = src ?? (product ? getPrimaryProductImageUrl(product) : undefined);
    const resolvedAlt = alt ?? product?.name ?? product?.id ?? '';
    const resolvedWidths = widths ?? imageContext?.widths ?? DEFAULT_IMAGE_WIDTHS;
    const resolvedHeights = heights ?? imageContext?.heights;
    const { overlay, below } = partitionImageChildren(children);
    if (!resolvedSrc) return null;
    return (
        <div
            ref={ref}
            data-slot="product-card-image"
            className={cn(
                'relative grid w-full min-w-0 grid-cols-[minmax(0,1fr)] gap-y-2',
                // Landscape: image sits right of the content column via order-last (DOM order unchanged —
                // consumers still author <Image> first).
                'group-data-[layout=landscape]/product-card:order-last group-data-[layout=landscape]/product-card:w-[90px] group-data-[layout=landscape]/product-card:shrink-0',
                className
            )}
            style={style}
            {...props}>
            <div
                data-slot="product-card-image-area"
                className="relative col-start-1 row-start-1 w-full min-w-0 overflow-hidden"
                style={{ aspectRatio }}>
                <DynamicImage
                    src={resolvedSrc}
                    alt={resolvedAlt}
                    widths={resolvedWidths}
                    heights={resolvedHeights}
                    // Left undefined when not a priority tile so the context can still elevate it.
                    loading={priority ? 'eager' : undefined}
                    priority={priority ? 'high' : undefined}
                    className="absolute inset-0 size-full"
                    imageProps={{
                        decoding: 'async',
                        className: cn(
                            'size-full group-data-[layout=landscape]/product-card:object-contain',
                            imageClassName
                        ),
                    }}
                />
                {overlay}
            </div>
            {below}
        </div>
    );
};
Image.displayName = 'ProductCard.Image';

// Badges

type BadgesProps = ComponentPropsWithRef<'div'>;

const Badges = ({ children, className, ref, ...props }: BadgesProps) => {
    const product = useProduct();
    const autoBadges = product ? getProductCardBadges(product) : [];
    const hasOverride = children !== undefined && children !== null && children !== false;
    if (!hasOverride && autoBadges.length === 0) return null;
    return (
        <div
            ref={ref}
            data-slot="product-card-badges"
            className={cn('absolute bottom-2 left-2 z-10 flex items-center gap-2 md:top-2 md:bottom-auto', className)}
            {...props}>
            {hasOverride
                ? children
                : autoBadges.map((b) => {
                      const badgeName = b.name ?? 'badge';
                      /* v8 ignore start */
                      const badgeText = b.callout ?? b.copy ?? ''; // filter guarantees callout|copy is truthy; ??'' is dead code
                      /* v8 ignore stop */
                      return (
                          <Badge
                              key={`${badgeName}-${badgeText}`}
                              type="custom"
                              style={{
                                  backgroundColor: b.background ?? undefined,
                                  color: b.color ?? undefined,
                                  padding: '4px 8px',
                              }}>
                              {b.callout ?? b.copy}
                          </Badge>
                      );
                  })}
        </div>
    );
};
Badges.displayName = 'ProductCard.Badges';

// Details

type DetailsProps = ComponentPropsWithRef<'div'>;

const Details = ({ className, ref, ...props }: DetailsProps) => (
    <div
        ref={ref}
        data-slot="product-card-details"
        className={cn(
            'flex w-full flex-col items-center gap-2 px-4 pb-4',
            'group-data-[layout=landscape]/product-card:w-auto group-data-[layout=landscape]/product-card:min-w-0 group-data-[layout=landscape]/product-card:flex-1 group-data-[layout=landscape]/product-card:items-start group-data-[layout=landscape]/product-card:gap-1 group-data-[layout=landscape]/product-card:py-2',
            className
        )}
        {...props}
    />
);
Details.displayName = 'ProductCard.Details';

// Brand

type BrandProps = Omit<TypographyProps, 'align' | 'weight'>;

const Brand = ({ children, className, variant = 'h4', as = 'p', ref, ...props }: BrandProps) => {
    const product = useProduct();
    const content = children ?? product?.brand ?? null;
    const isOutOfStock = product?.inventory?.orderable === false;
    if (!content) return null;
    return (
        <Typography
            as={as}
            variant={variant}
            weight="regular"
            align="center"
            ref={asTypographyRef(ref)}
            data-slot="product-card-brand"
            className={cn(
                'w-full overflow-hidden text-ellipsis',
                'group-data-[layout=landscape]/product-card:text-left',
                isOutOfStock && 'text-muted-foreground',
                className
            )}
            {...props}>
            {content}
        </Typography>
    );
};
Brand.displayName = 'ProductCard.Brand';

// Name

type NameProps = Omit<TypographyProps, 'align'>;

const Name = ({ children, className, ref, as = 'p', variant = 'p', weight = 'light', ...props }: NameProps) => {
    const product = useProduct();
    const content = children ?? product?.name ?? null;
    const isOutOfStock = product?.inventory?.orderable === false;
    if (!content) return null;
    return (
        <Typography
            as={as}
            variant={variant}
            weight={weight}
            align="center"
            ref={asTypographyRef(ref)}
            data-slot="product-card-name"
            className={cn(
                'w-full overflow-hidden line-clamp-2',
                'group-data-[layout=landscape]/product-card:text-left',
                isOutOfStock && 'text-muted-foreground',
                className
            )}
            {...props}>
            {content}
        </Typography>
    );
};
Name.displayName = 'ProductCard.Name';

// Price
interface PriceProps extends ComponentPropsWithRef<'div'> {
    currentPrice?: string;
    listPrice?: string;
    classNameText?: string;
}

const Price = ({ currentPrice, listPrice, className, ref, classNameText, ...props }: PriceProps) => {
    const product = useProduct();
    const { t, i18n } = useTranslation('product');
    const currency = product?.currency ?? 'GBP';

    // Delegate to `getPriceData` (same helper <ProductPrice> uses) so the card never disagrees with the
    // rest of the storefront on price. Resolves masters/sets to their lowest variant price.
    const priceData = product ? getPriceData(product) : undefined;
    const format = (value: number) => formatCurrency(value, i18n.language, currency);

    // A range is rendered as ONE span with no strikethrough — a `min – max` pair alongside a
    // was-price is meaningless. This mirrors <ProductPrice>'s `showPriceRange` rule.
    const isRange =
        priceData != null &&
        priceData.isRange &&
        priceData.maxPrice != null &&
        priceData.maxPrice > priceData.currentPrice;

    // Discounted range: the whole range carries the sale colour. Distinguishing from a plain range is
    // subtle — a `priceMax`-derived range sets `listPrice === maxPrice` (so `isOnSale` is an artifact),
    // whereas a genuine discount leaves `listPrice` at the promo price, differing from `maxPrice`.
    const isDiscountedRange =
        isRange && priceData.isOnSale && priceData.listPrice != null && priceData.listPrice !== priceData.maxPrice;

    let autoCurrent: string | undefined;
    if (isRange) {
        autoCurrent = t('price.range', {
            min: format(priceData.currentPrice),
            max: format(priceData.maxPrice as number),
        });
    } else if (priceData?.hasPrice) {
        autoCurrent = format(priceData.currentPrice);
    }

    const resolvedCurrent = currentPrice ?? autoCurrent;
    if (!resolvedCurrent) return null;

    // Never pair a strikethrough with a range.
    const autoListPrice =
        !isRange && priceData?.isOnSale && priceData.listPrice != null ? format(priceData.listPrice) : undefined;
    const resolvedList = listPrice ?? autoListPrice;
    const hasListPrice = typeof resolvedList === 'string' && resolvedList.length > 0;
    const currentAppearance = hasListPrice || isDiscountedRange ? 'sale' : undefined;

    return (
        <PriceDisplay.Root
            ref={ref}
            data-slot="product-card-price"
            className={cn(
                'w-full items-center',
                'group-data-[layout=landscape]/product-card:items-start group-data-[layout=landscape]/product-card:text-left',
                className
            )}
            {...props}>
            {hasListPrice ? (
                <PriceDisplay.Group className="justify-center">
                    <PriceDisplay.Original
                        data-slot="product-card-price-list"
                        className={cn('text-[length:var(--text-h5)]', classNameText)}>
                        {resolvedList}
                    </PriceDisplay.Original>
                    <PriceDisplay.Current
                        appearance={currentAppearance}
                        data-slot="product-card-price-current"
                        className={cn('text-[length:var(--text-h5)]', classNameText)}>
                        {resolvedCurrent}
                    </PriceDisplay.Current>
                </PriceDisplay.Group>
            ) : (
                <PriceDisplay.Current
                    appearance={currentAppearance}
                    data-slot="product-card-price-current"
                    className={cn('text-center text-[length:var(--text-h5)]', classNameText)}>
                    {resolvedCurrent}
                </PriceDisplay.Current>
            )}
        </PriceDisplay.Root>
    );
};
Price.displayName = 'ProductCard.Price';

// Availability

type AvailabilityProps = ComponentPropsWithRef<'p'>;

const Availability = ({ children, className, ref, ...props }: AvailabilityProps) => {
    const product = useProduct();
    const { t } = useTranslation('productCard');
    const isOrderable = product?.inventory?.orderable !== false;
    const content = children ?? (product && !isOrderable ? t('outOfStock', { defaultValue: 'Out of Stock' }) : null);
    if (!content) return null;
    return (
        <Typography
            as="p"
            variant="p"
            weight="light"
            align="center"
            ref={asTypographyRef(ref)}
            data-slot="product-card-availability"
            className={cn(
                'w-full text-snk-discounted',
                'group-data-[layout=landscape]/product-card:text-left',
                className
            )}
            {...props}>
            {content}
        </Typography>
    );
};
Availability.displayName = 'ProductCard.Availability';

// Namespace export

const ProductCard = {
    Root,
    Image,
    Badges,
    Action,
    AddToBagButton,
    Details,
    MatchScore,
    Brand,
    Name,
    Price,
    Attribute,
    Rating,
    Availability,
} as const;

export { ProductCard };
